import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { collectionData, DocumentReference, orderBy, query, Timestamp, where } from '@angular/fire/firestore';
import { FormGroup } from '@angular/forms';
import { IInventoryLocation } from '@inventory/models/inventory';
import { IInventoryLocationCreateForm, IInventoryLocationEditForm } from '@inventory/models/inventory-forms';
import { FireStoreCollection } from '@shared/models/firestore';
import { FirestoreService } from '@shared/services/firestore/firestore.service';

@Injectable({
	providedIn: 'root',
})
export class InventoryLocationsService extends FirestoreService<IInventoryLocation> {
	constructor() {
		super(FireStoreCollection.INVENTORY_LOCATIONS);
	}

	getInventoryLocationsByOrganisation(
		organisationRef: DocumentReference,
		showArchived: boolean = false,
	): Observable<Array<IInventoryLocation>> {
		let inventoryQuery = query(this.collectionRef, where('organisation', '==', organisationRef));
		inventoryQuery = query(inventoryQuery, where('archived', '==', showArchived));
		inventoryQuery = query(inventoryQuery, orderBy('createdDate', 'desc'));

		return collectionData<IInventoryLocation>(inventoryQuery, { idField: 'id' });
	}

	async createInventoryLocation(
		organisationRef: DocumentReference,
		inventoryLocation: FormGroup<IInventoryLocationCreateForm>,
	): Promise<DocumentReference<IInventoryLocation>> {
		const nameValue = inventoryLocation.controls.name.value as string;
		const descriptionValue = inventoryLocation.controls.description.value as string | null;

		return this.create({
			name: nameValue,
			nameLower: nameValue.toLocaleLowerCase(),
			description: descriptionValue || null,
			archived: false,
			organisation: organisationRef,
			createdDate: Timestamp.now(),
			updatedDate: Timestamp.now(),
		});
	}

	async updateInventoryLocation(inventoryLocationId: string, inventoryLocation: FormGroup<IInventoryLocationEditForm>): Promise<void> {
		const nameValue = inventoryLocation.controls.name.value as string;
		const descriptionValue = inventoryLocation.controls.description.value as string | null;
		const archivedValue = inventoryLocation.controls.archived.value as boolean;

		return this.update(inventoryLocationId, {
			name: nameValue,
			nameLower: nameValue.toLocaleLowerCase(),
			description: descriptionValue || null,
			archived: archivedValue ?? false,
			updatedDate: Timestamp.now(),
		});
	}

	async archiveInventoryLocation(inventoryLocationId: string): Promise<void> {
		return this.update(inventoryLocationId, {
			archived: true,
			updatedDate: Timestamp.now(),
		});
	}

	async unArchiveInventoryLocation(inventoryLocationId: string): Promise<void> {
		return this.update(inventoryLocationId, {
			archived: false,
			updatedDate: Timestamp.now(),
		});
	}
}
